import * as Analytics from 'analytics/analytics'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import Switch from 'LuxKit/Switch/Switch'
import BodyText from 'LuxKit/Typography/BodyText'
import React, { ChangeEventHandler, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import OfferPageLoyaltyStayUpgradeModal, { OfferPageLoyaltyStayUpgradeModalResult } from './OfferPageLoyaltyStayUpgradeModal'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'

interface Props {
  packageView: App.OfferPackageOptionView
}

function OfferPageLoyaltyStayUpgradeCallOutBannerAction({
  packageView,
}: Props) {
  const showModal = useContext(ModalContext)
  const intl = useIntl()
  const {
    selectedTargetUpgradeType,
    toggleTargetStayUpgrade,
  } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)
  const applyUpgrade = useCallback((upgradeType: App.LoyaltyStayUpgradeType | undefined) => {
    toggleTargetStayUpgrade(upgradeType)
    Analytics.trackClientEvent({
      subject: `loyalty_${upgradeType}_upgrade`,
      action: upgradeType ? 'toggled_on' : 'toggled_off',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [toggleTargetStayUpgrade])
  const handleUpgradeToggle = useCallback<ChangeEventHandler<HTMLInputElement>>(async(event) => {
    let upgradeType: App.LoyaltyStayUpgradeType | undefined
    switch (packageView.loyaltyUpgradeAvailability?.upgradeType) {
      case 'room-only':
        upgradeType = 'room'
        break
      case 'package-only':
        upgradeType = 'package'
        break
    }
    if (upgradeType) {
      const toggled = event.target.checked
      applyUpgrade(toggled ? upgradeType : undefined)
    }
  }, [packageView?.loyaltyUpgradeAvailability?.upgradeType, applyUpgrade])
  const openOptionsModal = useCallback(async() => {
    if (packageView.loyaltyUpgradeAvailability?.upgradeType !== 'room-and-package') return

    const result = await showModal<OfferPageLoyaltyStayUpgradeModalResult>(<OfferPageLoyaltyStayUpgradeModal
      upgradeAvailabilityView={packageView.loyaltyUpgradeAvailability}
      selectedTargetUpgradeType={selectedTargetUpgradeType}
      originPackage={packageView.package}
    />)

    if (result) {
      applyUpgrade(result.upgradeType)
    }
  }, [applyUpgrade, packageView, selectedTargetUpgradeType, showModal])

  const viewOptionsLabel = intl.formatMessage({
    id: 'feG0PQSEmB',
    defaultMessage: 'View options',
    description: 'Button text to view room or package upgrade options for loyalty members',
  })

  const roomLabel = intl.formatMessage({
    id: '3+F7gZrm5U',
    defaultMessage: 'Room',
    description: 'Button label when room upgrade is selected for loyalty members',
  })

  const packageLabel = intl.formatMessage({
    id: 'Y6ouNQ1bQ+',
    defaultMessage: 'Package',
    description: 'Button label when package upgrade is selected for loyalty members',
  })

  const applyUpgradeLabel = intl.formatMessage({
    id: 'OAE+tTvxpC',
    defaultMessage: 'Apply upgrade',
    description: 'Label next to toggle switch to apply loyalty stay upgrade',
  })

  const upgradeAppliedLabel = intl.formatMessage({
    id: 'XGCpT0tZ0U',
    defaultMessage: 'Upgrade applied',
    description: 'Label next to toggle switch when loyalty stay upgrade is applied',
  })

  if (!packageView.loyaltyUpgradeAvailability) return null

  return <>
    {packageView.loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && <TextButton
      kind="secondary"
      variant="dark"
      size="small"
      shape="round"
      startIcon={!!selectedTargetUpgradeType && <SolidCheckCircleIcon />}
      endIcon={<LineAngleRightIcon />}
      onClick={openOptionsModal}
    >
      {!selectedTargetUpgradeType && viewOptionsLabel}
      {!!selectedTargetUpgradeType && <>
        {selectedTargetUpgradeType === 'room' && roomLabel}
        {selectedTargetUpgradeType === 'package' && packageLabel}
      </>}
    </TextButton>}
    {packageView.loyaltyUpgradeAvailability.upgradeType !== 'room-and-package' && <Group
      direction="horizontal"
      verticalAlign="center"
      gap={8}
      as="label"
    >
      <CSSBreakpoint min="desktop">
        <BodyText variant="medium" wrap="no-wrap">
          {!selectedTargetUpgradeType && applyUpgradeLabel}
          {!!selectedTargetUpgradeType && upgradeAppliedLabel}
        </BodyText>
      </CSSBreakpoint>
      <Switch checked={!!selectedTargetUpgradeType} onChange={handleUpgradeToggle} />
    </Group>}
  </>
}

export default OfferPageLoyaltyStayUpgradeCallOutBannerAction
